import React, { useState } from "react";
import { Card, Tabs, Button, Row, Col, Typography, message, Dropdown, MenuProps, Space } from "antd";
import { FileTextOutlined, TrophyOutlined, DownloadOutlined, ExportOutlined } from "@ant-design/icons";
import { MedicalCondition, PDPMRankingAnalysis } from "../../types/codeIdentifier";
import CategoricalAnalysisWithPdf from "./CategoricalAnalysisWithPdf";
import PDPMRankingView from "./PDPMRankingView";
import { pdfReportService } from "../../services/pdfReportService";
import { exportService } from "../../services/exportService";

const { Title } = Typography;

interface ResultsContainerProps {
  finalEvaluation: MedicalCondition[];
  pdpmRankingAnalysis: PDPMRankingAnalysis | null;
  patientNames: string;
  s3InputFiles: string[];
  loadingFinalEvaluation: boolean;
  onPreviousStep: () => void;
}

const ResultsContainer: React.FC<ResultsContainerProps> = ({
  finalEvaluation,
  pdpmRankingAnalysis,
  patientNames,
  s3InputFiles,
  loadingFinalEvaluation,
  onPreviousStep
}) => {
  const [activeTab, setActiveTab] = useState("categorical");
  const [exporting, setExporting] = useState(false);

  const getExportData = () => ({
    finalEvaluation,
    pdpmRankingAnalysis,
    patientNames,
    generatedBy: 'CodePilot AI Analysis',
    facilityName: undefined // Can be enhanced later with organization data
  });

  const handleExport = async (key: string) => {
    const exportData = getExportData();
    
    try {
      setExporting(true);
      
      switch (key) {
        case 'categorical-pdf':
          // Use improved jsPDF service with proper text wrapping
          pdfReportService.generateCategoricalReport(exportData);
          message.success('Categorical PDF report downloaded successfully!');
          break;
        case 'pdpm-pdf':
          if (!pdpmRankingAnalysis) {
            message.error('No PDPM ranking analysis available for this patient.');
            return;
          }
          // Use improved jsPDF service with proper text wrapping
          pdfReportService.generatePDPMReport(exportData);
          message.success('PDPM PDF report downloaded successfully!');
          break;
        case 'categorical-csv':
          exportService.exportCategoricalToCSV(exportData);
          message.success('Categorical CSV exported successfully!');
          break;
        case 'pdpm-csv':
          if (!pdpmRankingAnalysis) {
            message.error('No PDPM ranking analysis available for this patient.');
            return;
          }
          exportService.exportPDPMToCSV(exportData);
          message.success('PDPM CSV exported successfully!');
          break;
        case 'categorical-excel':
          exportService.exportCategoricalToExcel(exportData);
          message.success('Categorical Excel file exported successfully!');
          break;
        case 'pdpm-excel':
          if (!pdpmRankingAnalysis) {
            message.error('No PDPM ranking analysis available for this patient.');
            return;
          }
          exportService.exportPDPMToExcel(exportData);
          message.success('PDPM Excel file exported successfully!');
          break;
        case 'complete-excel':
          exportService.exportAllToExcel(exportData);
          message.success('Complete Excel file exported successfully!');
          break;
        default:
          message.error('Unknown export format');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      message.error(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'pdf-group',
      label: 'PDF Reports',
      type: 'group',
    },
    {
      key: 'categorical-pdf',
      label: 'Categorical Analysis (PDF)',
      icon: <FileTextOutlined style={{ color: '#52c41a' }} />,
    },
    {
      key: 'pdpm-pdf',
      label: 'PDPM Ranking Analysis (PDF)',
      icon: <TrophyOutlined style={{ color: '#faad14' }} />,
      disabled: !pdpmRankingAnalysis,
    },
    {
      type: 'divider',
    },
    {
      key: 'csv-group',
      label: 'CSV Exports',
      type: 'group',
    },
    {
      key: 'categorical-csv',
      label: 'Categorical Analysis (CSV)',
      icon: <FileTextOutlined style={{ color: '#1890ff' }} />,
    },
    {
      key: 'pdpm-csv',
      label: 'PDPM Ranking Analysis (CSV)',
      icon: <TrophyOutlined style={{ color: '#1890ff' }} />,
      disabled: !pdpmRankingAnalysis,
    },
    {
      type: 'divider',
    },
    {
      key: 'excel-group',
      label: 'Excel Exports',
      type: 'group',
    },
    {
      key: 'categorical-excel',
      label: 'Categorical Analysis (Excel)',
      icon: <FileTextOutlined style={{ color: '#52c41a' }} />,
    },
    {
      key: 'pdpm-excel',
      label: 'PDPM Ranking Analysis (Excel)',
      icon: <TrophyOutlined style={{ color: '#52c41a' }} />,
      disabled: !pdpmRankingAnalysis,
    },
    {
      key: 'complete-excel',
      label: 'Complete Analysis (Excel)',
      icon: <ExportOutlined style={{ color: '#722ed1' }} />,
    },
  ];

  const cardTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', wordBreak: 'break-word', whiteSpace: 'normal' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ 
          background: 'linear-gradient(135deg, #1890ff, #40a9ff)',
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <FileTextOutlined style={{ color: 'white', fontSize: '18px' }} />
        </div>
        <div>
          <Title level={4} style={{ margin: 0, color: '#1f1f1f' }}>
            ICD-10 Analysis Results
          </Title>
          {patientNames && (
            <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
              Patient: <span style={{ fontWeight: '500', color: '#1890ff' }}>{patientNames}</span>
            </div>
          )}
        </div>
      </div>
      <Dropdown 
        menu={{ 
          items: exportMenuItems, 
          onClick: ({ key }) => handleExport(key) 
        }}
        placement="bottomRight"
        trigger={['click']}
      >
        <Button
          type="primary"
          icon={<ExportOutlined />}
          loading={exporting}
          style={{ 
            background: 'linear-gradient(135deg, #52c41a, #73d13d)',
            borderColor: '#52c41a',
            borderRadius: '8px',
            fontWeight: '500',
            height: '40px',
            padding: '0 20px',
            fontSize: '14px',
            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
            border: 'none'
          }}
          onMouseEnter={(e) => {
            if (!exporting) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #73d13d, #95de64)';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (!exporting) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #52c41a, #73d13d)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(82, 196, 26, 0.3)';
            }
          }}
        >
          {exporting ? 'Exporting...' : 'Export Results'}
        </Button>
      </Dropdown>
    </div>
  );

  return (
    <div style={{ marginBottom: "24px" }}>
      <Card
        title={cardTitle}
        style={{ 
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
        }}
      >
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            size="large"
            tabBarStyle={{
              marginBottom: "24px",
              borderBottom: "2px solid #f0f0f0"
            }}
            items={[
              {
                key: 'categorical',
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '500' }}>
                    <FileTextOutlined style={{ color: '#1890ff' }} />
                    Categorical Analysis
                  </span>
                ),
                children: (
                  <CategoricalAnalysisWithPdf 
                    finalEvaluation={finalEvaluation}
                    loadingFinalEvaluation={loadingFinalEvaluation}
                    s3Keys={s3InputFiles}
                  />
                )
              },
              {
                key: 'ranked',
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '500' }}>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    PDPM Ranking
                  </span>
                ),
                children: (
                  <PDPMRankingView 
                    pdpmRankingAnalysis={pdpmRankingAnalysis}
                    loadingFinalEvaluation={loadingFinalEvaluation}
                    finalEvaluation={finalEvaluation}
                  />
                )
              }
            ]}
          />
        </Card>
        <div style={{ 
          marginTop: "24px", 
          display: "flex", 
          justifyContent: "end",
          maxWidth: "1400px",
          margin: "24px auto 0 auto"
        }}>
          <Button 
            onClick={onPreviousStep}
            style={{
              height: '40px',
              padding: '0 20px',
              borderRadius: '8px',
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            ← Back to Upload
          </Button>
        </div>
    </div>
  );
};

export default ResultsContainer; 